import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { removeItem, setItem } from "../helpers/data_management";
import { updateCurrentSalaryDialog } from "../redux/actions/dialog_action";
import { newGetUserInfo } from "../redux/actions/user_action";
import * as userType from "../redux/types/user_type";

function validateJWT(token) {
  try {
    if (!token || typeof token !== "string" || token.split(".").length !== 3) {
      console.error("Invalid JWT token format");
      return null;
    }

    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const parsedPayload = JSON.parse(decoded);

    // Check if token has required fields
    if (
      !parsedPayload ||
      typeof parsedPayload !== "object" ||
      !parsedPayload.user_id
    ) {
      console.error("JWT token missing required fields");
      return null;
    }

    // Check token expiration - handle both standard 'exp' and custom 'expiry_at' fields
    const currentTime = Date.now();

    // Standard JWT exp field (Unix timestamp in seconds)
    if (parsedPayload.exp && parsedPayload.exp * 1000 < currentTime) {
      console.error("JWT token has expired (exp field)");
      return null;
    }

    // Custom expiry_at field (ISO string format)
    if (parsedPayload.expiry_at) {
      const expiryTime = new Date(parsedPayload.expiry_at).getTime();
      if (expiryTime < currentTime) {
        console.error("JWT token has expired (expiry_at field)");
        return null;
      }
    }

    return parsedPayload;
  } catch (e) {
    console.error("JWT token validation failed:", e);
    return null;
  }
}

/**
 * Custom hook to handle auto-login via authorization token in URL
 * @param {string} authorizationToken - The JWT token from URL query params
 * @returns {Object} - { isReady: boolean, userId: string | null }
 */
function useAutoLogin(authorizationToken) {
  const [isReady, setIsReady] = useState(false);
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state?.user?.user);

  useEffect(() => {
    if (!authorizationToken) {
      setIsReady(true);
      return;
    }

    // Validate and set the authorization token on mount
    const decoded = validateJWT(authorizationToken);
    if (!decoded || !decoded.user_id) {
      console.error("Invalid authorization token");
      setIsReady(true);
      return;
    }

    const tokenUserId = String(decoded.user_id);
    const currentToken = Cookies.get("token");
    const currentUserId = reduxUser?.id ? String(reduxUser.id) : null;

    // Check if token needs to be replaced
    if (currentToken !== authorizationToken) {
      // Clear authentication state for token switching (minimal clearing)
      removeItem(userType.USER_TOKEN);
      removeItem(userType.USER_PROFILE);
      Cookies.remove("token");
      Cookies.remove(process.env.NEXT_PUBLIC_JWT_COOKIE, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".hiredly.com",
        path: "/",
      });
      // Clear user state in Redux without full logout side effects
      dispatch({ type: userType.LOGOUT });

      // Set new token immediately
      Cookies.set("token", authorizationToken);
      Cookies.set(process.env.NEXT_PUBLIC_JWT_COOKIE, authorizationToken, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".hiredly.com",
        path: "/",
        expires: 7,
      });
      setItem(userType.USER_TOKEN, authorizationToken);

      // Disable salary dialog
      localStorage.removeItem("showCurrentSalaryDialogAfterJump");
      dispatch(updateCurrentSalaryDialog(false));

      // Force user info refresh
      dispatch(newGetUserInfo())
        .then(() => {
          setIsReady(true);
        })
        .catch((error) => {
          console.error("Token refresh failed:", error);
          setIsReady(true);
        });
    } else {
      // Token already set, mark as ready
      setIsReady(true);
    }
  }, [authorizationToken, dispatch]);

  return {
    isReady,
    userId: reduxUser?.id ? String(reduxUser.id) : null,
  };
}

export default useAutoLogin;
